import React, { useState, useEffect, useContext } from "react";
import {
  Table,
  Card,
  Statistic,
  Tag,
  Typography,
  Row,
  Col,
  Progress,
  Dropdown,
  Menu,
  Button,
  Modal,
  Form,
  Select,
  Input,
  InputNumber,
  message,
  Tooltip,
  Space,
  Divider,
  Avatar,
  Empty,
  Alert,
  Badge,
  DatePicker,
  Upload,
  Popconfirm,
} from "antd";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import { LineWave } from "react-loader-spinner";
import {
  DollarCircleOutlined,
  ProfileOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  AlertOutlined,
  MoreOutlined,
  UserOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  LineChartOutlined,
  FilterOutlined,
  FileTextOutlined,
  SearchOutlined,
  PieChartOutlined,
  FallOutlined,
  BankOutlined,
  DownloadOutlined,
  FireOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  UploadOutlined,
  FileExcelOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import GoogleSheetsService from "../utils/googleSheetsService";
import { Bar, Doughnut } from "react-chartjs-2";
import { Chart as ChartJS } from "chart.js/auto";
import { useParams } from "react-router-dom";
import { calculateLoanPayments, calculatePrincipalFromPayment } from "../utils/loanCalculations";
import DueTodayDashboard from "../components/DueTodayDashboard";
import moment from "moment";
import { TransactionHooks } from "../utils/transactionHooks";

const { Title, Text } = Typography;
const { Option } = Select;

// Zambian theme colors based on flag (toned down version)
const zambianColors = {
  green: "#4d8c4a", // Softer Green
  red: "#d16b6b", // Softer Red
  orange: "#e99e54", // Softer Orange
  black: "#444444", // Softer Black
  white: "#FFFFFF", // White for contrast
  lightGreen: "#80b782", // Lighter green for hover states
  lightOrange: "#f4c28e", // Lighter orange for hover states
};

const LoansPage = () => {
  const { company, user, accounts, updateAccountBalance } =
    useContext(AppContext);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [summaryVisible, setSummaryVisible] = useState(true);
  const [chartView, setChartView] = useState("amount"); // 'amount' or 'count'
  const [searchText, setSearchText] = useState("");
  const [filteredLoans, setFilteredLoans] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedOfficer, setSelectedOfficer] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  const token = localStorage.getItem("token");
  const { status } = useParams();
  const navigate = useNavigate();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [form] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const googleSheetsService = new GoogleSheetsService();
  const [users, setUsers] = useState([]);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [bulkUploadProgress, setBulkUploadProgress] = useState(0);
  const [approvalLoading, setApprovalLoading] = useState(false);

  // Fetch all users when component mounts
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(
          `https://zedlenders.pickmesms.com/users?fields=*.*&filter[role][name][_neq]=Borrower&filter[company][_eq]=${company.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setUsers(response.data.data);
      } catch (error) {
        console.error("Failed to fetch users:", error);
      }
    };

    if (company?.id && token) {
      fetchUsers();
    }
  }, [company?.id, token]);

  const paymentMethods = [
    { label: "Mobile Money", value: "mobile_money" },
    { label: "Bank Transfer", value: "bank_transfer" },
    { label: "Cash", value: "cash" },
    { label: "Gift Card", value: "gift_card" },
    { label: "Home Essentials", value: "home_essentials" },
  ];

  const handleChangeStatus = (record, action) => {
    setSelectedLoan(record);
    if (action === "approve") {
      setShowStatusModal(true);
    } else if (action === "reject") {
      setShowRejectModal(true);
    }
  };

  const handleRejectLoan = async (values) => {
    try {
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/loans/${selectedLoan.id}`,
        {
          loan_status: 4,
          rejection_reason: values.rejection_reason,
          rejection_date: new Date().toISOString().split("T")[0],
          rejected_by: user.user_id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      message.success("Loan rejected successfully");
      setShowRejectModal(false);
      rejectForm.resetFields();

      // Navigate to rejected loans page
      navigate("/loans/4");
    } catch (error) {
      console.error("Error rejecting loan:", error);
      message.error("Failed to reject loan");
    }
  };

  const handleEditRejectedLoan = (record) => {
    // Navigate to edit loan page with the rejected loan data
    navigate(`/loan/edit/${record.id}`, {
      state: {
        loanData: record,
        isResubmission: true,
        rejectionReason: record.rejection_reason,
      },
    });
  };

  const handleDeleteRejectedLoan = async (loanId) => {
    try {
      // Check if there are any transactions associated with this loan
      const transactionsResponse = await axios.get(
        `https://zedlenders.pickmesms.com/items/transactions?filter[loan][_eq]=${loanId}&fields=id`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const transactions = transactionsResponse.data.data || [];

      if (transactions.length > 0) {
        message.error(
          `Cannot delete this loan. There are ${transactions.length} transaction(s) associated with it. Please delete the transactions first.`
        );
        return;
      }

      // Check if there's collateral associated with this loan
      const loanResponse = await axios.get(
        `https://zedlenders.pickmesms.com/items/loans/${loanId}?fields=collateral`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const collateral = loanResponse.data.data?.collateral || [];

      if (collateral.length > 0) {
        message.warning(
          "This loan has associated collateral. The collateral will remain in the system but will no longer be linked to this loan."
        );
      }

      // Delete the loan
      await axios.delete(
        `https://zedlenders.pickmesms.com/items/loans/${loanId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      message.success("Rejected loan deleted successfully");
      window.location.reload();
    } catch (error) {
      console.error("Error deleting rejected loan:", error);
      message.error("Failed to delete loan. Please try again.");
    }
  };

  const handleSendForReview = async (record) => {
    try {
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/loans/${record.id}`,
        {
          assigned_to: user.user_id, // Assign to current user
          loan_status: 1, // Update status to "Pending Approval"
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Navigate to Pending Approval page
      navigate("/loans/1");
      message.success("Loan sent for review successfully");
    } catch (error) {
      message.error("Failed to send loan for review");
      console.error("Send for review error:", error);
    }
  };

  const handleStatusUpdate = async (values) => {
    setApprovalLoading(true);
    try {
      // Find the selected account
      const selectedAccount = accounts.find((acc) => acc.id === values.account);

      if (!selectedAccount) {
        message.error("Selected account not found");
        return;
      }

      // Calculate loan product processing fees
      let productProcessingFee = 0;
      if (
        selectedLoan.loan_product?.processing_fee &&
        selectedLoan.loan_product.processing_fee > 0
      ) {
        if (selectedLoan.loan_product.processing_fee_type === "percentage") {
          productProcessingFee =
            selectedLoan.amount *
            (parseFloat(selectedLoan.loan_product.processing_fee) / 100);
        } else if (selectedLoan.loan_product.processing_fee_type === "fixed") {
          productProcessingFee = parseFloat(
            selectedLoan.loan_product.processing_fee
          );
        }
      }

      // Calculate fees
      const transferFees = values.transfer_fees || 0;
      const manualAdminFees = values.admin_fees || 0;
      const totalAdminFees = manualAdminFees + productProcessingFee;
      const loanAmount = selectedLoan.amount;
      const totalAccountDeduction = loanAmount + transferFees;
      const borrowerReceives = loanAmount - totalAdminFees;

      // Check account balance against total deduction (loan + transfer fees)
      if (selectedAccount.balance < totalAccountDeduction) {
        message.error(
          `Insufficient balance in ${
            selectedAccount.name
          }. Required: ZMW ${totalAccountDeduction.toLocaleString()}, Available: ZMW ${parseFloat(
            selectedAccount.balance
          ).toLocaleString()}`
        );
        return;
      }

      // Calculate new account balance (deduct loan amount + transfer fees, then add total admin fees back)
      const newAccountBalance =
        parseFloat(selectedAccount.balance) -
        totalAccountDeduction +
        totalAdminFees;

      // Create the main disbursement transaction
      const response = await axios.post(
        `https://zedlenders.pickmesms.com/items/transactions`,
        {
          ...values,
          loan: selectedLoan.id,
          amount: totalAccountDeduction, // Total amount deducted from account
          disbursed_amount: borrowerReceives, // Amount actually received by borrower
          transfer_fees: transferFees,
          admin_fees: totalAdminFees,
          manual_admin_fees: manualAdminFees,
          product_processing_fee: productProcessingFee,
          user: user.id,
          is_loan_transaction: true,
          transaction_type: "Loan Disbursement",
          transaction_status: "Completed",
          reference_number: Math.floor(Math.random() * 1000000),
          new_amount: newAccountBalance,
          company: company?.id,
          from_account: values.account,
          transaction_date: new Date().toISOString().split("T")[0],
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // If there are transfer fees, create a separate transaction for them
      if (transferFees > 0) {
        await axios.post(
          `https://zedlenders.pickmesms.com/items/transactions`,
          {
            loan: selectedLoan.id,
            amount: transferFees,
            user: user.id,
            is_loan_transaction: false,
            transaction_type: "Transfer Fees",
            transaction_status: "Completed",
            reference_number: Math.floor(Math.random() * 1000000),
            company: company?.id,
            from_account: values.account,
            transaction_date: new Date().toISOString().split("T")[0],
            notes: `Transfer fees for loan disbursement #${selectedLoan.id}`,
            payment_method: values.payment_method,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      // If there are admin fees, create a separate transaction for them (as inflow/revenue)
      if (totalAdminFees > 0) {
        await axios.post(
          `https://zedlenders.pickmesms.com/items/transactions`,
          {
            loan: selectedLoan.id,
            amount: totalAdminFees, // Positive amount - this is revenue/inflow
            manual_admin_fees: manualAdminFees,
            product_processing_fee: productProcessingFee,
            user: user.id,
            is_loan_transaction: false,
            transaction_type: "Admin Fees",
            transaction_status: "Completed",
            reference_number: Math.floor(Math.random() * 1000000),
            company: company?.id,
            to_account: values.account, // Use to_account since this is money coming INTO the account
            transaction_date: new Date().toISOString().split("T")[0],
            notes: `Admin fees revenue from loan disbursement #${selectedLoan.id} (Manual: ${manualAdminFees}, Product: ${productProcessingFee})`,
            payment_method: values.payment_method,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      // Close modal and reset form early
      setShowStatusModal(false);
      form.resetFields();

      if (response.data.data.transaction_status === "Completed") {
        // Update the loan status first
        // Use provided approval date or default to current date
        const approvalDate = values.approval_date
          ? values.approval_date.format("YYYY-MM-DD")
          : new Date().toISOString().split("T")[0];

        console.log(
          "DEBUG: Manual approval date from form:",
          values.approval_date
        );
        console.log("DEBUG: Formatted approval date:", approvalDate);
        console.log(
          "DEBUG: Date object passed to calculation:",
          new Date(approvalDate)
        );
        console.log(
          "DEBUG: Today's date for comparison:",
          new Date().toISOString().split("T")[0]
        );

        // Calculate proper payment amounts based on loan product settings
        const loanProduct = selectedLoan.loan_product;
        const paymentDetails = calculateLoanPayments(
          loanProduct,
          selectedLoan.amount,
          new Date(approvalDate)
        );

        console.log("DEBUG: Calculated payment details:", paymentDetails);

        await axios.patch(
          `https://zedlenders.pickmesms.com/items/loans/${selectedLoan.id}`,
          {
            loan_status: 7,
            approval_date: approvalDate,
            assigned_to: values.assigned_to || null,
            next_payment_date: paymentDetails.next_payment_date,
            next_payment_amount: paymentDetails.next_payment_amount,
            total_amount_due: paymentDetails.total_amount_due,
            interest_amount: paymentDetails.interest_amount,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // Update the account balance in backend
        await axios.patch(
          `https://zedlenders.pickmesms.com/items/accounts/${values.account}`,
          {
            balance: newAccountBalance,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // Update account balance in AppContext state
        updateAccountBalance(values.account, newAccountBalance);

        // Notify navbar to refresh today's disbursement total
        window.dispatchEvent(
          new CustomEvent("disbursement:created", {
            detail: {
              loanId: selectedLoan.id,
              amount: totalAccountDeduction,
              disbursedAmount: borrowerReceives,
            },
          })
        );

        // Record double-entry accounting for disbursement
        try {
          await TransactionHooks.onLoanDisbursed({
            loanId: selectedLoan.id,
            amount: selectedLoan.amount,
            date: new Date(approvalDate),
            company: company?.id,
            token,
            accountId: values.account,
          });
        } catch (doubleEntryError) {
          console.error("Error recording double-entry for disbursement:", doubleEntryError);
          // Continue even if double-entry fails
        }

        // Check if this is a Home Essentials loan and deduct stock
        if (selectedLoan.is_commodity_loan) {
          try {
            // Fetch loan details to get commodity list
            const loanDetailsResponse = await axios.get(
              `https://zedlenders.pickmesms.com/items/loans/${selectedLoan.id}?fields=*,commodity_list.*`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );

            const commodityList = loanDetailsResponse.data.data.commodity_list;
            console.log("Commodity list:", commodityList);

            // Fetch the loan_commodity_details to get the quantities for each commodity
            const loanCommodityDetailsResponse = await axios.get(
              `https://zedlenders.pickmesms.com/items/loan_commodity_details?filter[loan_id]=${selectedLoan.id}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );

            const loanCommodityDetails = loanCommodityDetailsResponse.data.data;
            console.log("Loan commodity details:", loanCommodityDetails);

            if (commodityList && commodityList.length > 0) {
              // Update each commodity's stock by reducing the quantity
              const stockUpdatePromises = [];

              // First, create a map of commodity details for easier lookup
              const commodityDetailsMap = {};

              // Populate the map from loan_commodity_details
              if (loanCommodityDetails && loanCommodityDetails.length > 0) {
                loanCommodityDetails.forEach((detail) => {
                  commodityDetailsMap[detail.commodity_id] = {
                    quantity: detail.quantity,
                    selling_price: detail.selling_price,
                  };
                });
              }

              // Process each commodity
              for (const commodityId of commodityList) {
                console.log("Processing commodity ID:", commodityId);

                // Skip if no valid ID
                if (!commodityId) {
                  console.error("Invalid commodity ID found");
                  continue;
                }

                // Find the commodity details with quantity
                const commodityDetail = commodityDetailsMap[commodityId];

                // Skip if no details found
                if (!commodityDetail) {
                  console.error(
                    `No details found for commodity ${commodityId}`
                  );
                  continue;
                }

                const quantityToDeduct = commodityDetail.quantity || 0;

                if (quantityToDeduct <= 0) {
                  console.warn(
                    `Zero or negative quantity for commodity ${commodityId}, skipping`
                  );
                  continue;
                }

                try {
                  // Get current commodity details first
                  const response = await axios.get(
                    `https://zedlenders.pickmesms.com/items/commodities/${commodityId}`,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );

                  const currentStock = response.data.data;
                  console.log(
                    `Current stock for commodity ${commodityId}:`,
                    currentStock
                  );

                  // Make sure current quantity is a valid number
                  const currentQuantity = parseInt(currentStock.quantity) || 0;

                  // Calculate new quantity
                  const newQuantity = Math.max(
                    0,
                    currentQuantity - quantityToDeduct
                  );

                  console.log(
                    `Updating quantity for commodity ${commodityId}: ${currentQuantity} -> ${newQuantity} (deducting ${quantityToDeduct})`
                  );

                  // Add the update promise to our array
                  stockUpdatePromises.push(
                    axios.patch(
                      `https://zedlenders.pickmesms.com/items/commodities/${commodityId}`,
                      { quantity: newQuantity },
                      { headers: { Authorization: `Bearer ${token}` } }
                    )
                  );
                } catch (error) {
                  console.error(
                    `Error processing commodity ${commodityId}:`,
                    error
                  );
                }
              }

              // Wait for all updates to complete
              if (stockUpdatePromises.length > 0) {
                await Promise.all(stockUpdatePromises);
                message.success(
                  "Loan approved and commodity stock updated successfully!"
                );
              } else {
                message.warning(
                  "Loan approved but no commodity stocks were updated. Please check inventory manually.",
                  10
                );
              }
            }
          } catch (stockUpdateError) {
            console.error(
              "Failed to update commodity stock:",
              stockUpdateError
            );
            message.warning(
              "Loan approved successfully, but failed to update commodity stock. Please adjust inventory manually.",
              10
            );
          }
        } else {
          message.success("Loan approved successfully!");
        }

        // Update Google Sheets with loan status change
        try {
          // Fetch complete loan data with expanded relationships to get full branch object
          const fullLoanResponse = await axios.get(
            `https://zedlenders.pickmesms.com/items/loans/${selectedLoan.id}?fields=*,branch.*,borrower.*,loan_product.*`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          const fullLoanData = fullLoanResponse.data.data;

          const updatedLoanData = {
            ...fullLoanData, // Use complete loan data with expanded relationships
            loan_status: 7,
            approval_date: new Date().toISOString().split("T")[0],
          };

          await googleSheetsService.updateLoanInSheet(
            selectedLoan.id,
            updatedLoanData
          );
          console.log("Loan status updated in Google Sheets successfully");
        } catch (googleSheetsError) {
          console.error(
            "Failed to update loan status in Google Sheets:",
            googleSheetsError
          );
          // Don't show error to user as the main operation succeeded
        }

        // Navigate to active loans page
        navigate("/loans/7");
      }
    } catch (error) {
      message.error("Failed to update loan status");
      console.error("Update error:", error);
    } finally {
      setApprovalLoading(false);
    }
  };

  let cardTitle = "";
  let statusIcon = null;
  let statusColor = zambianColors.green;

  switch (status) {
    case "7":
      cardTitle = "Active Loans";
      statusIcon = <SyncOutlined />;
      statusColor = zambianColors.green;
      break;
    case "8":
      cardTitle = "Overdue Loans";
      statusIcon = <AlertOutlined />;
      statusColor = zambianColors.red;
      break;
    case "9":
      cardTitle = "Defaulted Loans";
      statusIcon = <FallOutlined />;
      statusColor = "#000000";
      break;
    case "1":
      cardTitle = "Pending Approval";
      statusIcon = <ProfileOutlined />;
      statusColor = zambianColors.orange;
      break;
    case "4":
      cardTitle = "Rejected Loans";
      statusIcon = <CloseCircleOutlined />;
      statusColor = zambianColors.red;
      break;
    case "2":
      cardTitle = "Loan Applications";
      statusIcon = <UserOutlined />;
      statusColor = zambianColors.lightGreen;
      break;
    case "10":
      cardTitle = "🎉 Settled Loans";
      statusIcon = <CheckCircleOutlined />;
      statusColor = "#52c41a"; // Brighter success green
      break;
    default:
      cardTitle = "All Loans";
      statusIcon = <ProfileOutlined />;
      statusColor = zambianColors.green;
  }

  const statusMap = {
    7: {
      text: "Active",
      color: "green",
      icon: <SyncOutlined spin />,
    },
    1: {
      text: "Pending Approval",
      color: "orange",
      icon: <ProfileOutlined />,
    },
    3: {
      text: "Closed",
      color: zambianColors.black,
      icon: <CheckCircleOutlined />,
    },
    8: { text: "Overdue", color: zambianColors.red, icon: <AlertOutlined /> },
    2: {
      text: "New Application",
      color: zambianColors.lightGreen,
      icon: <UserOutlined />,
    },
    9: {
      text: "Defaulted",
      color: "#000000",
      icon: <FallOutlined />,
    },
    10: {
      text: "🎉 Settled",
      color: "green",
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
    },
    4: {
      text: "Rejected",
      color: "red",
      icon: <CloseCircleOutlined />,
    },
    12: {
      text: "App Application",
      color: "purple",
      icon: <ProfileOutlined />,
    },
  };

  const handleViewDetails = (record) => {
    navigate(`/loan/${record.id}`);
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const clearFilters = () => {
    setSearchText("");
    setSelectedOfficer(null);
    setDateRange(null);
  };

  // Apply all filters whenever filter state changes
  useEffect(() => {
    let filtered = [...loans];

    // Filter by search text (borrower name, loan ID, or product name)
    if (searchText) {
      filtered = filtered.filter((loan) => {
        const borrowerName = loan.borrower
          ? `${loan.borrower.first_name} ${loan.borrower.last_name}`.toLowerCase()
          : "";
        const loanId = String(loan.id).toLowerCase();
        const productName = (loan.loan_product?.loan_name || "").toLowerCase();
        const searchLower = searchText.toLowerCase();
        return (
          borrowerName.includes(searchLower) ||
          loanId.includes(searchLower) ||
          productName.includes(searchLower)
        );
      });
    }

    // Filter by loan officer
    if (selectedOfficer) {
      filtered = filtered.filter((loan) => {
        if (selectedOfficer === "unassigned") {
          return !loan.assigned_to || !loan.assigned_to.id;
        }
        return loan.assigned_to?.id === selectedOfficer;
      });
    }

    // Filter by next payment date range
    if (dateRange && dateRange.length === 2) {
      filtered = filtered.filter((loan) => {
        if (!loan.next_payment_date) return false;
        const paymentDate = moment(loan.next_payment_date);
        return paymentDate.isBetween(dateRange[0], dateRange[1], "day", "[]");
      });
    }

    setFilteredLoans(filtered);
  }, [loans, searchText, selectedOfficer, dateRange]);

  const handleExportToExcel = () => {
    if (!filteredLoans || filteredLoans.length === 0) {
      message.warning("No loans to export");
      return;
    }

    try {
      // Prepare data for export
      const exportData = filteredLoans.map((loan, index) => ({
        "S/N": index + 1,
        "Borrower Name": loan.borrower
          ? `${loan.borrower.first_name} ${loan.borrower.last_name}`
          : "No borrower assigned",
        "Loan Product": loan.loan_product?.loan_name || "Unknown",
        "Amount (ZMW)": loan.amount,
        Status: statusMap[loan.loan_status]?.text || "Unknown",
        "Application Date": loan.application_date || "Not recorded",
        "Approval Date": loan.approval_date || "Not approved",
        "Next Payment Date":
          loan.loan_status === 10
            ? "N/A - Loan Settled"
            : loan.next_payment_date || "N/A",
        "Last Payment Date": (() => {
          if (loan.transactions && loan.transactions.length > 0) {
            const paymentTransactions = loan.transactions.filter(
              (t) =>
                t.transaction_type &&
                !t.transaction_type.includes("Disbursement") &&
                !t.transaction_type.includes("Admin Fees") &&
                !t.transaction_type.includes("Transfer Fees")
            );

            if (paymentTransactions.length > 0) {
              const sortedPayments = paymentTransactions.sort(
                (a, b) =>
                  new Date(b.transaction_date) - new Date(a.transaction_date)
              );
              return new Date(
                sortedPayments[0].transaction_date
              ).toLocaleDateString("en-ZM", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              });
            }
          }
          return "No payments yet";
        })(),
        "Paid Amount (ZMW)": loan.paid_amount || 0,
        "Payment Progress (%)":
          Math.round((loan.paid_amount / loan.amount) * 100) || 0,
        "Loan Officer": loan.assigned_to
          ? `${loan.assigned_to.first_name} ${loan.assigned_to.last_name}`
          : "Unassigned",
        "Collateral Items": loan.collateral?.length || 0,
        "Is Commodity Loan": loan.is_commodity_loan ? "Yes" : "No",
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const columnWidths = [
        { wch: 5 }, // S/N
        { wch: 25 }, // Borrower Name
        { wch: 20 }, // Loan Product
        { wch: 15 }, // Amount
        { wch: 15 }, // Status
        { wch: 15 }, // Application Date
        { wch: 15 }, // Approval Date
        { wch: 18 }, // Next Payment Date
        { wch: 18 }, // Last Payment Date
        { wch: 15 }, // Paid Amount
        { wch: 18 }, // Payment Progress
        { wch: 20 }, // Loan Officer
        { wch: 15 }, // Collateral Items
        { wch: 18 }, // Is Commodity Loan
      ];
      worksheet["!cols"] = columnWidths;

      // Add the worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Loans");

      // Generate filename with current date and status
      const currentDate = new Date().toISOString().split("T")[0];
      const filename = `${cardTitle.replace(/\s+/g, "_")}_${currentDate}.xlsx`;

      // Export the file
      XLSX.writeFile(workbook, filename);

      message.success(
        `Successfully exported ${filteredLoans.length} loans to Excel`
      );
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export loans to Excel");
    }
  };

  useEffect(() => {
    const fetchLoans = async () => {
      try {
        const response = await axios.get(
          `https://zedlenders.pickmesms.com/items/loans?filter[loan_status]=${status}&fields=*,custom_interest_rate,interest_applied,collateral.id,borrower.id,borrower.first_name,borrower.last_name,loan_product.loan_name,loan_product.*,gps_trackers.id,gps_trackers.gps_code,gps_trackers.status,assigned_to.*,transactions.transaction_date,transactions.transaction_type&filter[company]=${company?.id}&sort=-id&limit=-1`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log("Fetched loans:", response.data.data);

        // Sort loans to put due today loans first
        const sortedLoans = response.data.data.sort((a, b) => {
          const aIsToday =
            a.next_payment_date &&
            moment(a.next_payment_date).isSame(moment(), "day");
          const bIsToday =
            b.next_payment_date &&
            moment(b.next_payment_date).isSame(moment(), "day");

          // Due today loans first
          if (aIsToday && !bIsToday) return -1;
          if (!aIsToday && bIsToday) return 1;

          // Then by ID (newest first)
          return b.id - a.id;
        });

        setLoans(sortedLoans);
        setFilteredLoans(sortedLoans);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchLoans();
  }, [status, company?.id, token]);

  const getSummaryStats = () => {
    const totalAmount = loans.reduce((sum, loan) => sum + loan.amount, 0);

    // Calculate total interest expected from all loans
    const totalInterestExpected = loans.reduce((sum, loan) => {
      // Comprehensive validation
      if (!loan || !loan.loan_product || !loan.amount) return sum;

      const loanProduct = loan.loan_product;
      const principal = parseFloat(loan.amount);

      // Validate essential fields
      if (isNaN(principal) || principal <= 0) return sum;
      if (
        !loanProduct.interest_rate ||
        isNaN(parseFloat(loanProduct.interest_rate))
      )
        return sum;

      const interestRate = parseFloat(loanProduct.interest_rate);
      if (interestRate <= 0) return sum;

      // Calculate total interest based on loan product type
      let interestAmount = 0;

      try {
        if (
          loanProduct.interest_type === "simple" &&
          loanProduct.simple_interest_method === "flat_rate"
        ) {
          // Simple interest with flat rate
          interestAmount = principal * (interestRate / 100);
        } else if (loanProduct.interest_type === "compound") {
          // Validate required fields for compound interest
          const resolvedLoanTerm = loan.loan_term ?? loanProduct.loan_term;
          if (
            !resolvedLoanTerm ||
            !loanProduct.term_unit ||
            !loanProduct.payment_frequency ||
            !loanProduct.compound_frequency
          ) {
            // Fallback to simple interest if compound data is incomplete
            interestAmount = principal * (interestRate / 100);
          } else {
            // For compound interest, calculate using compound formula over the loan term
            const daysPerUnit = {
              days: 1,
              weeks: 7,
              months: 30.44,
              years: 365.25,
            };
            const periodsPerYear = {
              daily: 365,
              weekly: 52,
              bi_weekly: 26,
              "3_weeks": 17.39,
              monthly: 12,
              yearly: 1,
            };
            const paymentIntervalDays = {
              daily: 1,
              weekly: 7,
              bi_weekly: 14,
              "3_weeks": 21,
              monthly: 30.44,
              yearly: 365.25,
            };

            const loanTerm = parseFloat(resolvedLoanTerm);
            if (isNaN(loanTerm) || loanTerm <= 0) {
              interestAmount = principal * (interestRate / 100);
            } else {
              const term_in_days =
                loanTerm * (daysPerUnit[loanProduct.term_unit] || 30.44);
              const payment_interval_in_days =
                paymentIntervalDays[loanProduct.payment_frequency] || 30.44;
              const number_of_payments = Math.ceil(
                term_in_days / payment_interval_in_days
              );

              const m = periodsPerYear[loanProduct.compound_frequency] || 12;
              const p = periodsPerYear[loanProduct.payment_frequency] || 12;
              const r_annual = interestRate / 100;

              if (m > 0 && p > 0 && number_of_payments > 0) {
                const effective_rate_per_payment =
                  Math.pow(1 + r_annual / m, m / p) - 1;

                const r = effective_rate_per_payment;
                const n = number_of_payments;

                if (r > 0 && n > 0) {
                  const payment_amount =
                    (principal * (r * Math.pow(1 + r, n))) /
                    (Math.pow(1 + r, n) - 1);
                  const total_payment = payment_amount * n;
                  interestAmount = total_payment - principal;
                } else {
                  // Fallback to simple interest
                  interestAmount = principal * (interestRate / 100);
                }
              } else {
                // Fallback to simple interest
                interestAmount = principal * (interestRate / 100);
              }
            }
          }
        } else {
          // Default to simple interest
          interestAmount = principal * (interestRate / 100);
        }

        // Ensure result is valid
        if (isNaN(interestAmount) || interestAmount < 0) {
          interestAmount = 0;
        }
      } catch (error) {
        console.warn("Error calculating interest for loan:", loan.id, error);
        interestAmount = 0;
      }

      return sum + interestAmount;
    }, 0);

    const overdueLoans = loans.filter((loan) => loan.loan_status === 8).length;
    const riskRating = overdueLoans > loans.length * 0.2 ? "High" : "Normal";

    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              background: statusColor,
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ color: zambianColors.white, fontSize: "14px", marginBottom: "8px" }}>
                  {cardTitle}
                </div>
                <div style={{ color: zambianColors.white, fontSize: "32px", fontWeight: "bold" }}>
                  {loans.length}
                </div>
                <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "12px", marginTop: "4px" }}>
                  Total count in this category
                </div>
              </div>
              <div style={{ color: zambianColors.white, fontSize: "24px" }}>
                {statusIcon}
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              background: zambianColors.orange,
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ color: zambianColors.white, fontSize: "14px", marginBottom: "8px" }}>
                  Total Portfolio
                </div>
                <div style={{ color: zambianColors.white, fontSize: "32px", fontWeight: "bold" }}>
                  <span style={{ fontSize: "18px" }}>ZMW</span> {totalAmount.toLocaleString()}
                </div>
                <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "12px", marginTop: "4px" }}>
                  Total value of {loans.length} loans
                </div>
              </div>
              <div style={{ color: zambianColors.white, fontSize: "24px" }}>
                <DollarCircleOutlined />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              background: zambianColors.lightGreen,
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ color: zambianColors.white, fontSize: "14px", marginBottom: "8px" }}>
                  Total Interest Expected
                </div>
                <div style={{ color: zambianColors.white, fontSize: "32px", fontWeight: "bold" }}>
                  <span style={{ fontSize: "18px" }}>ZMW</span> {Math.round(totalInterestExpected).toLocaleString()}
                </div>
                <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "12px", marginTop: "4px" }}>
                  Expected interest from {loans.length} loans
                </div>
              </div>
              <div style={{ color: zambianColors.white, fontSize: "24px" }}>
                <LineChartOutlined />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              background: riskRating === "High" ? zambianColors.red : zambianColors.black,
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ color: zambianColors.white, fontSize: "14px", marginBottom: "8px" }}>
                  Risk Rating
                </div>
                <div style={{ color: zambianColors.white, fontSize: "32px", fontWeight: "bold" }}>
                  {riskRating}
                </div>
                <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "12px", marginTop: "4px" }}>
                  {overdueLoans} overdue out of {loans.length}
                </div>
              </div>
              <div style={{ color: zambianColors.white, fontSize: "24px" }}>
                <AlertOutlined />
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    );
  };

  const getFilteredColumns = () => {
    const baseColumns = [
      {
        title: "Borrower",
        dataIndex: "borrower",
        key: "borrower",
        render: (_, record) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            {/* <Avatar
              icon={<UserOutlined />}
              style={{
                backgroundColor: record.borrower ? zambianColors.green : "#ccc",
                marginRight: 8,
              }}
            /> */}
            <div style={{ flex: 1 }}>
              {record.borrower ? (
                <>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Text
                      strong
                      onClick={() =>
                        navigate(`/borrower/${record.borrower.id}`)
                      }
                      style={{ color: zambianColors.green, cursor: "pointer" }}
                    >
                      {`${record.borrower.first_name} ${record.borrower.last_name}`}
                    </Text>
                    {record.gps_trackers && record.gps_trackers.length > 0 && (
                      <Tooltip title="GPS Tracker">
                        <EnvironmentOutlined
                          style={{
                            color: "#52c41a",
                            fontSize: "16px",
                            cursor: "help",
                          }}
                        />
                      </Tooltip>
                    )}
                  </div>
                </>
              ) : (
                <Text strong style={{ color: zambianColors.red }}>
                  No borrower assigned
                </Text>
              )}
            </div>
          </div>
        ),
      },
      // {
      //   title: "Loan Type",
      //   dataIndex: "loan_product",
      //   key: "loan_product",
      //   render: (type) =>
      //     type ? (
      //       <Tag
      //         color={zambianColors.black}
      //         style={{ borderRadius: "5px", padding: "0 10px" }}
      //       >
      //         {type.loan_name}
      //       </Tag>
      //     ) : (
      //       <Tag
      //         color={zambianColors.red}
      //         style={{ borderRadius: "5px", padding: "0 10px" }}
      //       >
      //         Unknown
      //       </Tag>
      //     ),
      // },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        render: (amount) => (
          <Text strong style={{ color: zambianColors.black }}>
            ZMW {amount ? amount.toLocaleString() : '0'}
          </Text>
        ),
        sorter: (a, b) => a.amount - b.amount,
      },
      {
        title: "Rate",
        key: "interest_rate",
        render: (_, record) => {
          const appliedRate = record.interest_applied;
          const customRate = record.custom_interest_rate;
          const displayRate = appliedRate ?? customRate ?? record.loan_product?.interest_rate ?? 0;
          const isCustom = customRate != null && customRate !== record.loan_product?.interest_rate;

          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Text strong style={{ color: zambianColors.black }}>
                {parseFloat(displayRate)}%
              </Text>
              {isCustom && (
                <Tooltip title="Custom interest rate applied">
                  <Tag color="orange" style={{ fontSize: '10px', padding: '0 4px', margin: 0 }}>
                    Custom
                  </Tag>
                </Tooltip>
              )}
            </div>
          );
        },
        width: 90,
        sorter: (a, b) => {
          const rateA = a.interest_applied ?? a.custom_interest_rate ?? a.loan_product?.interest_rate ?? 0;
          const rateB = b.interest_applied ?? b.custom_interest_rate ?? b.loan_product?.interest_rate ?? 0;
          return rateA - rateB;
        },
      },
      {
        title: "Status",
        dataIndex: "loan_status",
        key: "status",
        render: (status, record) => (
          <Space direction="horizontal" size="small">
            <Tag
              icon={statusMap[status]?.icon}
              color={statusMap[status]?.color}
              style={{
                borderRadius: "5px",
                padding: status === 10 ? "4px 12px" : "0 10px",
                minWidth: status === 10 ? "110px" : "auto",
                textAlign: "center",
              }}
            >
              {statusMap[status]?.text}
            </Tag>
            {/* Show reloaned tag to the right for active loans that have been reloaned */}
            {status === 7 && record.reloaned > 0 && (
              <Tag
                color="orange"
                style={{
                  borderRadius: "5px",
                  padding: "0 8px",
                  fontSize: "11px",
                }}
              >
                Reloaned ({record.reloaned})
              </Tag>
            )}
          </Space>
        ),
        width: 120,
        filters: Object.entries(statusMap).map(([key, status]) => ({
          text: status.text,
          value: key,
        })),
        onFilter: (value, record) =>
          String(record.loan_status) === String(value),
      },
      // Conditionally include Rejection Reason column only for rejected loans
      ...(status === "4"
        ? [
            {
              title: "Rejection Reason",
              dataIndex: "rejection_reason",
              key: "rejection_reason",
              render: (reason, record) => (
                <div style={{ maxWidth: "200px" }}>
                  {reason ? (
                    <Tooltip title={reason} placement="topLeft">
                      <Text
                        type="secondary"
                        style={{
                          fontSize: "12px",
                          lineHeight: "1.4",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {reason}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      No reason provided
                    </Text>
                  )}
                  {record.rejection_date && (
                    <div style={{ marginTop: "4px" }}>
                      <Text type="secondary" style={{ fontSize: "10px" }}>
                        Rejected: {new Date(record.rejection_date).toLocaleDateString()}
                      </Text>
                    </div>
                  )}
                </div>
              ),
              width: 220,
            },
          ]
        : []),
      // Conditionally include Next Payment column only for non-settled loans
      ...(status !== "10"
        ? [
            {
              title: "Next Payment",
              dataIndex: "next_payment_date",
              key: "next_payment",
              sorter: (a, b) => {
                const ta = a.next_payment_date
                  ? new Date(a.next_payment_date).getTime()
                  : Number.MAX_SAFE_INTEGER;
                const tb = b.next_payment_date
                  ? new Date(b.next_payment_date).getTime()
                  : Number.MAX_SAFE_INTEGER;
                return ta - tb;
              },
              sortDirections: ["ascend", "descend"],
              render: (date, record) => {
                const isToday = date && moment(date).isSame(moment(), "day");
                const isOverdue =
                  date && moment(date).isBefore(moment(), "day");

                return date ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      ...(isToday && {
                        animation: "urgentBounce 2s ease-in-out infinite",
                        background: "linear-gradient(45deg, #ff9a8b, #ffecd2)",
                        padding: "4px 8px",
                        borderRadius: "8px",
                        border: "2px solid #ff4d4f",
                      }),
                    }}
                  >
                    {isToday && (
                      <FireOutlined
                        style={{
                          color: "#ff4d4f",
                          marginRight: 4,
                          animation: "pulse 1s infinite",
                        }}
                      />
                    )}
                    <CalendarOutlined
                      style={{
                        color: isToday
                          ? "#ff4d4f"
                          : isOverdue
                          ? "#fa8c16"
                          : zambianColors.green,
                      }}
                    />
                    <span
                      style={{
                        marginLeft: 8,
                        fontWeight: isToday ? "bold" : "normal",
                        color: isToday
                          ? "#ff4d4f"
                          : isOverdue
                          ? "#fa8c16"
                          : "inherit",
                      }}
                    >
                      {new Date(date).toLocaleDateString("en-ZM", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                      })}
                    </span>
                    {isToday && (
                      <Tag
                        color="red"
                        size="small"
                        style={{
                          marginLeft: 4,
                          fontSize: "12px",
                          padding: "1px 4px",
                          lineHeight: 1,
                        }}
                      >
                        DUE TODAY
                      </Tag>
                    )}
                  </div>
                ) : (
                  "-"
                );
              },
            },
          ]
        : []),
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() => handleViewDetails(record)}
              disabled={status === "4"}
              style={{
                backgroundColor:
                  status === "4" ? "#f5f5f5" : zambianColors.lightGreen,
                borderColor: status === "4" ? "#d9d9d9" : zambianColors.green,
                color: status === "4" ? "#00000040" : undefined,
              }}
            >
              Details
            </Button>

            {status === "1" &&
              record.loan_product &&
              record.loan_product.requires_collateral &&
              record.collateral &&
              record.collateral.length > 0 && (
                <Button
                  size="small"
                  onClick={() =>
                    navigate(`/collateral/${record.collateral[0].id}`)
                  }
                  style={{
                    backgroundColor: zambianColors.orange,
                    color: "white",
                    borderColor: zambianColors.orange,
                  }}
                >
                  Collateral
                </Button>
              )}

            {status === "1" && (
              <>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handleChangeStatus(record, "approve")}
                  style={{
                    backgroundColor: zambianColors.lightGreen,
                    borderColor: zambianColors.green,
                    marginRight: 8,
                  }}
                >
                  Approve
                </Button>
                <Button
                  danger
                  size="small"
                  onClick={() => handleChangeStatus(record, "reject")}
                  style={{
                    backgroundColor: "#fff1f0",
                    borderColor: zambianColors.red,
                    color: zambianColors.red,
                  }}
                >
                  Reject
                </Button>
              </>
            )}

            {status === "2" && (
              <Button
                size="small"
                onClick={() => handleSendForReview(record)}
                style={{
                  backgroundColor: zambianColors.lightOrange,
                  color: "#444444",
                  borderColor: zambianColors.orange,
                }}
              >
                Review
              </Button>
            )}

            {status === "4" && (
              <>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handleEditRejectedLoan(record)}
                  style={{
                    backgroundColor: zambianColors.orange,
                    borderColor: zambianColors.orange,
                  }}
                >
                  Edit & Resubmit
                </Button>
                <Popconfirm
                  title="Delete this rejected loan?"
                  description="This action cannot be undone. Are you sure you want to delete this rejected loan?"
                  onConfirm={() => handleDeleteRejectedLoan(record.id)}
                  okText="Yes, Delete"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                  >
                    Delete
                  </Button>
                </Popconfirm>
              </>
            )}
          </Space>
        ),
      },
    ];

    return baseColumns;
  };

  // Group loans by type for the chart
  const getLoansByType = () => {
    const typeGroups = {};
    const typeAmounts = {};

    filteredLoans.forEach((loan) => {
      const typeName = loan.loan_product?.loan_name || "Unknown";
      if (!typeGroups[typeName]) {
        typeGroups[typeName] = 0;
        typeAmounts[typeName] = 0;
      }
      typeGroups[typeName]++;
      typeAmounts[typeName] += loan.amount;
    });

    return {
      types: Object.keys(typeGroups),
      counts: Object.values(typeGroups),
      amounts: Object.values(typeAmounts),
    };
  };

  const loanTypes = getLoansByType();

  const chartData = {
    labels: loanTypes.types,
    datasets: [
      {
        data: chartView === "amount" ? loanTypes.amounts : loanTypes.counts,
        backgroundColor: [
          zambianColors.green,
          zambianColors.orange,
          zambianColors.red,
          zambianColors.black,
          zambianColors.lightGreen,
          "#6A0DAD", // Purple for additional colors if needed
          "#4682B4", // Steel Blue
          "#8B4513", // Saddle Brown
        ],
        borderColor: "white",
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "right",
        labels: {
          boxWidth: 15,
          padding: 15,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.label || "";
            if (chartView === "amount") {
              return `${label}: ZMW ${context.raw.toLocaleString()}`;
            } else {
              return `${label}: ${context.raw} loans`;
            }
          },
        },
      },
    },
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <LineWave
          visible={true}
          height="100"
          width="100"
          color={zambianColors.green}
          ariaLabel="line-wave-loading"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: 24 }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: zambianColors.red }}>
              Error loading loans: {error.message}
            </span>
          }
        />
        <Button
          type="primary"
          onClick={() => window.location.reload()}
          style={{
            marginTop: 16,
            backgroundColor: zambianColors.green,
            borderColor: zambianColors.green,
          }}
        >
          Try Again
        </Button>
      </div>
    );
  }

  if (filteredLoans.length === 0) {
    return (
      <>
        <div style={{ padding: 24 }}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center" }}>
                {statusIcon}{" "}
                <span style={{ marginLeft: 8, color: statusColor }}>
                  {cardTitle}
                </span>
              </div>
            }
            bordered={false}
            style={{ textAlign: "center" }}
            extra={
              <Button
                icon={<FilterOutlined />}
                onClick={() => navigate("/loans/7")}
                style={{
                  borderColor: zambianColors.green,
                  color: zambianColors.green,
                }}
              >
                View Active Loans
              </Button>
            }
          >
            <Empty
              description={
                status === "10" ? (
                  <span>🎉 No settled loans yet - but that's coming soon!</span>
                ) : (
                  <span>No loans found in this category</span>
                )
              }
            />
            <Space style={{ marginTop: 16 }}>
              <Button
                type="primary"
                onClick={() => navigate("/addLoan")}
                style={{
                  backgroundColor: zambianColors.green,
                  borderColor: zambianColors.green,
                }}
              >
                Create New Loan
              </Button>
              <Button
                icon={<FileExcelOutlined />}
                onClick={() => setShowBulkUploadModal(true)}
              >
                Bulk Upload
              </Button>
            </Space>
          </Card>
        </div>

        {/* Bulk Upload Modal - Must be included in early return */}
        <Modal
          title="Bulk Loan Upload"
          open={showBulkUploadModal}
          onCancel={() => {
            setShowBulkUploadModal(false);
            setBulkUploadFile(null);
            setBulkUploadProgress(0);
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setShowBulkUploadModal(false);
                setBulkUploadFile(null);
                setBulkUploadProgress(0);
              }}
            >
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              icon={<UploadOutlined />}
              loading={bulkUploadLoading}
              disabled={!bulkUploadFile || bulkUploadLoading}
              onClick={handleBulkUpload}
              style={{
                backgroundColor: zambianColors.green,
                borderColor: zambianColors.green,
              }}
            >
              Process Upload
            </Button>,
          ]}
          width={700}
        >
          <Alert
            message="Bulk Loan Upload Instructions"
            description={
              <div>
                <p>Upload a CSV file with the following columns:</p>
                <ul style={{ marginBottom: 0 }}>
                  <li><strong>borrower_nrc:</strong> Borrower's NRC number (must exist in system)</li>
                  <li><strong>loan_product_name:</strong> Name of the loan product (must exist in system)</li>
                  <li><strong>amount:</strong> Loan amount</li>
                  <li><strong>custom_interest_rate:</strong> (Optional) Custom interest rate (e.g., 45, 292). Leave blank to use product default.</li>
                  <li><strong>application_date:</strong> Date in format YYYY-MM-DD</li>
                  <li><strong>loan_purpose:</strong> Purpose of the loan</li>
                  <li><strong>branch_name:</strong> Branch name (optional)</li>
                  <li><strong>loan_status:</strong> (Optional) Status code: 1=Pending, 7=Active, 8=Overdue, 10=Settled. Defaults to 1 if not provided.</li>
                  <li><strong>approval_date:</strong> (Optional) Approval date in format YYYY-MM-DD</li>
                </ul>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Divider orientation="left">Upload CSV File</Divider>

          <Upload
            accept=".csv"
            beforeUpload={(file) => {
              const isCsv = file.type === "text/csv" || file.name.endsWith(".csv");
              if (!isCsv) {
                message.error("You can only upload CSV files!");
                return Upload.LIST_IGNORE;
              }
              setBulkUploadFile(file);
              return false;
            }}
            onRemove={() => {
              setBulkUploadFile(null);
            }}
            fileList={bulkUploadFile ? [bulkUploadFile] : []}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Select CSV File</Button>
          </Upload>

          <div style={{ marginTop: 16 }}>
            <a
              href="/sample_loans_upload.csv"
              download="sample_loans_upload.csv"
              style={{ color: zambianColors.green }}
            >
              <FileExcelOutlined /> Download Sample Template
            </a>
          </div>

          {bulkUploadLoading && (
            <div style={{ marginTop: 16 }}>
              <Progress percent={bulkUploadProgress} status="active" />
              <p style={{ textAlign: "center", marginTop: 8 }}>
                Processing loans... Please don't close this window.
              </p>
            </div>
          )}
        </Modal>
      </>
    );
  }

  // Check if we're viewing active loans and have due today loans
  const showDueTodayDashboard = status === "7" && moment().isValid();

  return (
    <div className="loans-page" style={{ padding: 16 }}>
      {/* Full-page loading overlay */}
      {approvalLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <LineWave color={zambianColors.green} height={100} width={100} />
          <Text
            style={{
              color: zambianColors.white,
              fontSize: "20px",
              marginTop: "20px",
              fontWeight: "bold",
            }}
          >
            Processing loan approval...
          </Text>
          <Text
            style={{
              color: "rgba(255,255,255,0.8)",
              fontSize: "14px",
              marginTop: "8px",
            }}
          >
            Please wait while we update records and disburse funds
          </Text>
        </div>
      )}
      {/* Due Today Dashboard - Only show for active loans */}
      {/* {showDueTodayDashboard && (
        <Card
          style={{
            marginBottom: 24,
            background: "linear-gradient(135deg, #ff9a8b 0%, #fad0c4 100%)",
            border: "none",
            borderRadius: 12,
          }}
        >
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <FireOutlined
                  style={{
                    fontSize: 24,
                    color: "#ff4d4f",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
                <div>
                  <Title level={4} style={{ margin: 0, color: "#722ed1" }}>
                    🔥 Due Today Dashboard
                  </Title>
                  <Text style={{ color: "#8c8c8c" }}>
                    View loans requiring immediate attention
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Button
                type="primary"
                size="large"
                icon={<FireOutlined />}
                onClick={() => navigate("/due-today")}
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  borderRadius: 8,
                  boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                }}
              >
                View Due Today
              </Button>
            </Col>
          </Row>
        </Card>
      )} */}

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title
            level={3}
            style={{
              margin: 0,
              color: statusColor,
              display: "flex",
              alignItems: "center",
            }}
          >
            {statusIcon} <span style={{ marginLeft: 8 }}>{cardTitle}</span>
          </Title>
          <Text type="secondary">Showing {filteredLoans.length} loans</Text>
        </Col>
        <Col>
          <Space>
            <Tooltip title="Bulk Upload Loans">
              <Button
                icon={<FileExcelOutlined />}
                onClick={() => setShowBulkUploadModal(true)}
                style={{
                  backgroundColor: zambianColors.orange,
                  borderColor: zambianColors.orange,
                  color: "white",
                }}
              >
                Bulk Upload
              </Button>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportToExcel}
                style={{
                  backgroundColor: zambianColors.green,
                  borderColor: zambianColors.green,
                  color: "white",
                }}
              >
                Export
              </Button>
            </Tooltip>
            <Tooltip title="Toggle Summary View">
              <Button
                icon={<PieChartOutlined />}
                type={summaryVisible ? "primary" : "default"}
                onClick={() => setSummaryVisible(!summaryVisible)}
                style={
                  summaryVisible
                    ? {
                        backgroundColor: zambianColors.lightGreen,
                        borderColor: zambianColors.green,
                      }
                    : {}
                }
              />
            </Tooltip>
            <Tooltip title="Toggle Filter Options">
              <Button
                icon={<FilterOutlined />}
                type={filterVisible ? "primary" : "default"}
                onClick={() => setFilterVisible(!filterVisible)}
                style={
                  filterVisible
                    ? {
                        backgroundColor: zambianColors.lightOrange,
                        borderColor: zambianColors.orange,
                      }
                    : {}
                }
              />
            </Tooltip>
            <Input
              placeholder="Search by name or ID"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: 200 }}
              allowClear
            />
          </Space>
        </Col>
      </Row>

      {summaryVisible && getSummaryStats()}

      {/* Filters Section */}
      {filterVisible && (
        <Card style={{ marginBottom: 16, marginTop: 16 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={6}>
              <Select
                placeholder="Filter by Loan Officer"
                value={selectedOfficer}
                onChange={setSelectedOfficer}
                allowClear
                style={{ width: "100%" }}
                showSearch
                optionFilterProp="children"
              >
                <Select.Option key="unassigned" value="unassigned">
                  <Text style={{ color: zambianColors.red }}>
                    🔴 Unassigned Loans
                  </Text>
                </Select.Option>
                {users.map((u) => (
                  <Select.Option key={u.id} value={u.id}>
                    <Space>
                      <UserOutlined />
                      {u.first_name} {u.last_name}
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={10}>
              <DatePicker.RangePicker
                value={dateRange}
                onChange={setDateRange}
                style={{ width: "100%" }}
                placeholder={["Next Payment From", "Next Payment To"]}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Button
                icon={<CloseCircleOutlined />}
                onClick={clearFilters}
                style={{ width: "100%" }}
              >
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card
            title="Loans List"
            bordered={false}
            style={{
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              borderRadius: "8px",
              height: "100%",
            }}
            headStyle={{
              backgroundColor: "#f0f8f0",
              color: zambianColors.green,
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            }}
          >
            <Table
              columns={getFilteredColumns()}
              dataSource={filteredLoans}
              rowKey="id"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: filteredLoans.length,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} loans`,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  if (size !== pageSize) {
                    setPageSize(size);
                    setCurrentPage(1); // Reset to first page when changing page size
                  }
                },
                showQuickJumper: true,
              }}
              scroll={{ x: 800 }}
              expandable={{
                expandedRowRender: (record) => (
                  <div
                    style={{
                      margin: 0,
                      padding: 16,
                      backgroundColor: "#f9f9f9",
                      borderRadius: 8,
                    }}
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={8}>
                        <div>
                          <Text strong style={{ color: zambianColors.green }}>
                            Application Date:
                          </Text>
                          <p>{record.application_date || "Not recorded"}</p>
                        </div>
                        <div>
                          <Text strong style={{ color: zambianColors.green }}>
                            Collateral:
                          </Text>
                          <p>{record.collateral?.length || 0} items</p>
                        </div>
                      </Col>
                      <Col xs={24} sm={8}>
                        <div>
                          <Text strong style={{ color: zambianColors.green }}>
                            Payment Progress:
                          </Text>
                          <Progress
                            percent={
                              record.loan_status === 10
                                ? 100
                                : Math.round(
                                    (record.paid_amount / record.amount) * 100
                                  )
                            }
                            status={
                              record.loan_status === 8
                                ? "exception"
                                : record.loan_status === 10
                                ? "success"
                                : "active"
                            }
                            strokeColor={
                              record.loan_status === 8
                                ? zambianColors.red
                                : record.loan_status === 10
                                ? "#52c41a"
                                : zambianColors.green
                            }
                            size="small"
                            format={
                              record.loan_status === 10
                                ? () => "🎉 PAID IN FULL"
                                : undefined
                            }
                          />
                        </div>
                        {/* <div style={{ marginTop: 10 }}>
                          <Text strong style={{ color: zambianColors.green }}>
                            Last Payment:
                          </Text>
                          <p>{(() => {
                            if (record.transactions && record.transactions.length > 0) {
                              // Filter for payment transactions (exclude disbursements)
                              const paymentTransactions = record.transactions.filter(t => 
                                t.transaction_type && 
                                !t.transaction_type.includes('Disbursement') &&
                                !t.transaction_type.includes('Admin Fees') &&
                                !t.transaction_type.includes('Transfer Fees')
                              );
                              
                              if (paymentTransactions.length > 0) {
                                // Find the most recent payment date
                                const sortedPayments = paymentTransactions.sort((a, b) => 
                                  new Date(b.transaction_date) - new Date(a.transaction_date)
                                );
                                return new Date(sortedPayments[0].transaction_date).toLocaleDateString("en-ZM", {
                                  year: "numeric",
                                  month: "2-digit", 
                                  day: "2-digit",
                                });
                              }
                            }
                            return "No payments yet";
                          })()}</p>
                        </div> */}
                      </Col>
                      <Col xs={24} sm={8}>
                        <div>
                          <Text strong style={{ color: zambianColors.green }}>
                            Loan Officer:
                          </Text>
                          <p>
                            {record.assigned_to
                              ? `${record.assigned_to.first_name} ${record.assigned_to.last_name}`
                              : "Unassigned"}
                          </p>
                        </div>
                        <div style={{ marginTop: 10 }}>
                          <Text strong style={{ color: zambianColors.green }}>
                            Risk Rating:
                          </Text>
                          <Tag
                            color={zambianColors.orange}
                            style={{ marginTop: 5 }}
                          >
                            Medium Risk
                          </Tag>
                        </div>
                      </Col>
                    </Row>
                  </div>
                ),
              }}
            />
          </Card>
        </Col>

        {/* Charts section commented out to give table full width */}
      </Row>

      <Modal
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              color: zambianColors.green,
            }}
          >
            <CheckCircleOutlined style={{ marginRight: 8 }} />
            Approve & Disburse Funds
          </div>
        }
        open={showStatusModal}
        onCancel={() => setShowStatusModal(false)}
        onOk={() => form.submit()}
        confirmLoading={approvalLoading}
        okText={approvalLoading ? "Processing..." : "Approve & Disburse"}
        okButtonProps={{
          style: {
            backgroundColor: zambianColors.green,
            borderColor: zambianColors.green,
          },
        }}
      >
        <Form form={form} onFinish={handleStatusUpdate} layout="vertical">
          <Form.Item
            label="Disbursement Method"
            name="payment_method"
            rules={[
              { required: true, message: "Please select payment method" },
            ]}
          >
            <Select options={paymentMethods} />
          </Form.Item>

          <Form.Item
            label="Disbursement Account"
            name="account"
            rules={[
              { required: true, message: "Please select disbursement account" },
            ]}
          >
            <Select placeholder="Select the account to disburse from">
              {(() => {
                //console.log("Current user:", user);
                //console.log("Available accounts:", accounts);

                // Filter accounts to only show those that belong to the current user and are active
                const filteredAccounts = accounts?.filter((account) => {
                  // First check if account is active (default to active if no status field)
                  const isActive =
                    account.status === "active" || !account.status;
                  if (!isActive) {
                    return false;
                  }

                  // If the account has no users assigned, show it to everyone
                  if (
                    !account.users ||
                    !Array.isArray(account.users) ||
                    account.users.length === 0
                  ) {
                    return true;
                  }

                  // Check if current user is in the assigned users for this account
                  return account.users.some((userAssignment) => {
                    const userId = user.user_id; // Correctly access the user's ID

                    // Handle both object and string ID cases
                    const assignedUserId = userAssignment.directus_users_id?.id
                      ? userAssignment.directus_users_id.id
                      : userAssignment.directus_users_id;

                    return assignedUserId === userId;
                  });
                });

                return filteredAccounts?.map((account) => (
                  <Select.Option key={account.id} value={account.id}>
                    <Space>
                      <BankOutlined
                        style={{
                          color: account.is_default
                            ? zambianColors.green
                            : "#666",
                        }}
                      />
                      <span>
                        {account.name} {account.is_default ? "(Default)" : ""} -
                        Balance: ZMW{" "}
                        {parseFloat(account.balance).toLocaleString()}
                      </span>
                    </Space>
                  </Select.Option>
                ));
              })()}
            </Select>
          </Form.Item>

          <Form.Item
            label="Transfer Fees (ZMW)"
            name="transfer_fees"
            tooltip="Additional fees charged for the disbursement that will be deducted from the account but not from the loan amount"
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Enter transfer fees (optional)"
              min={0}
              step={10}
              formatter={(value) =>
                `ZMW ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/ZMW\s?|(,*)/g, "")}
            />
          </Form.Item>

          <Form.Item
            label="Manual Admin Fees (ZMW)"
            name="admin_fees"
            tooltip="Additional administrative fees on top of the loan product's processing fee"
            help={
              selectedLoan?.loan_product?.processing_fee &&
              selectedLoan.loan_product.processing_fee > 0
                ? `Note: Product processing fee of ${
                    selectedLoan.loan_product.processing_fee_type ===
                    "percentage"
                      ? `${selectedLoan.loan_product.processing_fee}%`
                      : `ZMW ${selectedLoan.loan_product.processing_fee}`
                  } will be automatically added`
                : "No product processing fee configured"
            }
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Enter admin fees (optional)"
              min={0}
              step={10}
              formatter={(value) =>
                `ZMW ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/ZMW\s?|(,*)/g, "")}
            />
          </Form.Item>

          <Form.Item name="notes" label="Status Notes">
            <Input.TextArea
              placeholder="Add any additional notes..."
              rows={4}
            />
          </Form.Item>

          <Form.Item
            name="approval_date"
            label="Approval Date"
            help="Leave blank to use current date"
          >
            <DatePicker
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
              placeholder="Select approval date (optional)"
            />
          </Form.Item>

          <Form.Item
            name="assigned_to"
            label="Assign Loan Officer"
            help="Select a user to assign this loan to for management"
          >
            <Select
              placeholder="Select loan officer (optional)"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {users.map((user) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name}
                  {user.role?.name && (
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      ({user.role.name})
                    </Text>
                  )}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {selectedLoan && (
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const transferFees = getFieldValue("transfer_fees") || 0;
                const manualAdminFees = getFieldValue("admin_fees") || 0;
                const loanAmount = selectedLoan.amount || 0;

                // Calculate loan product processing fees
                let productProcessingFee = 0;
                if (
                  selectedLoan.loan_product?.processing_fee &&
                  selectedLoan.loan_product.processing_fee > 0
                ) {
                  if (
                    selectedLoan.loan_product.processing_fee_type ===
                    "percentage"
                  ) {
                    productProcessingFee =
                      loanAmount *
                      (parseFloat(selectedLoan.loan_product.processing_fee) /
                        100);
                  } else if (
                    selectedLoan.loan_product.processing_fee_type === "fixed"
                  ) {
                    productProcessingFee = parseFloat(
                      selectedLoan.loan_product.processing_fee
                    );
                  }
                }

                const totalAdminFees = manualAdminFees + productProcessingFee;
                const totalDeduction = loanAmount + transferFees;
                const borrowerReceives = loanAmount - totalAdminFees;

                return (
                  <Alert
                    type="info"
                    message={`Loan Disbursement Summary`}
                    description={
                      <div>
                        <div>
                          <strong>Loan Amount:</strong> ZMW{" "}
                          {loanAmount.toLocaleString()}
                        </div>
                        {transferFees > 0 && (
                          <div>
                            <strong>Transfer Fees:</strong> ZMW{" "}
                            {transferFees.toLocaleString()}
                          </div>
                        )}
                        {manualAdminFees > 0 && (
                          <div>
                            <strong>Manual Admin Fees:</strong> ZMW{" "}
                            {manualAdminFees.toLocaleString()}
                          </div>
                        )}
                        {productProcessingFee > 0 && (
                          <div>
                            <strong>Product Processing Fee:</strong> ZMW{" "}
                            {productProcessingFee.toLocaleString()}
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              (
                              {selectedLoan.loan_product.processing_fee_type ===
                              "percentage"
                                ? `${selectedLoan.loan_product.processing_fee}%`
                                : `ZMW ${selectedLoan.loan_product.processing_fee}`}
                              )
                            </Text>
                          </div>
                        )}
                        {totalAdminFees > 0 &&
                          totalAdminFees !== manualAdminFees && (
                            <div>
                              <strong>Total Admin Fees:</strong> ZMW{" "}
                              {totalAdminFees.toLocaleString()}
                            </div>
                          )}
                        <div
                          style={{
                            marginTop: 8,
                            paddingTop: 8,
                            borderTop: "1px solid #d9d9d9",
                          }}
                        >
                          <div>
                            <strong>Total Account Deduction:</strong> ZMW{" "}
                            {totalDeduction.toLocaleString()}
                          </div>
                          <div>
                            <strong>Borrower Receives:</strong> ZMW{" "}
                            {borrowerReceives.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    }
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                );
              }}
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Rejection Modal */}
      <Modal
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              color: zambianColors.red,
            }}
          >
            <CloseCircleOutlined style={{ marginRight: 8 }} />
            Reject Loan Application
          </div>
        }
        open={showRejectModal}
        onCancel={() => setShowRejectModal(false)}
        onOk={() => rejectForm.submit()}
        okText="Reject Loan"
        okButtonProps={{
          danger: true,
          style: {
            backgroundColor: zambianColors.red,
            borderColor: zambianColors.red,
          },
        }}
      >
        <Form form={rejectForm} onFinish={handleRejectLoan} layout="vertical">
          <Form.Item
            label="Reason for Rejection"
            name="rejection_reason"
            rules={[
              {
                required: true,
                message: "Please provide a reason for rejection",
              },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Please explain why this loan is being rejected. This will help the borrower understand what needs to be improved for future applications."
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal
        title="Bulk Loan Upload"
        open={showBulkUploadModal}
        onCancel={() => {
          setShowBulkUploadModal(false);
          setBulkUploadFile(null);
          setBulkUploadProgress(0);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowBulkUploadModal(false);
              setBulkUploadFile(null);
              setBulkUploadProgress(0);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            icon={<UploadOutlined />}
            loading={bulkUploadLoading}
            disabled={!bulkUploadFile || bulkUploadLoading}
            onClick={handleBulkUpload}
            style={{
              backgroundColor: zambianColors.green,
              borderColor: zambianColors.green,
            }}
          >
            Process Upload
          </Button>,
        ]}
        width={700}
      >
        <Alert
          message="Bulk Loan Upload Instructions"
          description={
            <div>
              <p>Upload a CSV file with the following columns:</p>
              <ul style={{ marginBottom: 0 }}>
                <li><strong>borrower_nrc:</strong> Borrower's NRC number (must exist in system)</li>
                <li><strong>loan_product_name:</strong> Name of the loan product (must exist in system)</li>
                <li><strong>amount:</strong> Loan amount</li>
                <li><strong>custom_interest_rate:</strong> (Optional) Custom interest rate (e.g., 45, 292). Leave blank to use product default.</li>
                <li><strong>application_date:</strong> Date in format YYYY-MM-DD</li>
                <li><strong>loan_purpose:</strong> Purpose of the loan</li>
                <li><strong>branch_name:</strong> Branch name (optional)</li>
                <li><strong>loan_status:</strong> (Optional) Status code: 1=Pending, 7=Active, 8=Overdue, 10=Settled. Defaults to 1 if not provided.</li>
                <li><strong>approval_date:</strong> (Optional) Approval date in format YYYY-MM-DD</li>
              </ul>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Divider orientation="left">Upload CSV File</Divider>

        <Upload
          accept=".csv"
          beforeUpload={(file) => {
            const isCsv = file.type === "text/csv" || file.name.endsWith(".csv");
            if (!isCsv) {
              message.error("You can only upload CSV files!");
              return Upload.LIST_IGNORE;
            }
            setBulkUploadFile(file);
            return false;
          }}
          onRemove={() => {
            setBulkUploadFile(null);
          }}
          fileList={bulkUploadFile ? [bulkUploadFile] : []}
          maxCount={1}
        >
          <Button icon={<UploadOutlined />}>Select CSV File</Button>
        </Upload>

        <div style={{ marginTop: 16 }}>
          <a
            href="/sample_loans_upload.csv"
            download="sample_loans_upload.csv"
            style={{ color: zambianColors.green }}
          >
            <FileExcelOutlined /> Download Sample Template
          </a>
        </div>

        {bulkUploadLoading && (
          <div style={{ marginTop: 16 }}>
            <Progress percent={bulkUploadProgress} status="active" />
            <p style={{ textAlign: "center", marginTop: 8 }}>
              Processing loans... Please don't close this window.
            </p>
          </div>
        )}
      </Modal>

    </div>
  );

  const isMonthlyAmountRow = (row) => {
    const raw = (row.amount_type || row.amount_is_monthly || "").toString().trim().toLowerCase();
    return ["monthly", "true", "yes", "1"].includes(raw);
  };

  async function handleBulkUpload() {
    if (!bulkUploadFile) {
      message.error("Please select a CSV file first");
      return;
    }

    setBulkUploadLoading(true);
    setBulkUploadProgress(10);

    try {
      // Parse CSV file
      const parseResult = await new Promise((resolve, reject) => {
        Papa.parse(bulkUploadFile, {
          header: true,
          complete: resolve,
          error: reject,
          skipEmptyLines: true,
        });
      });

      const csvData = parseResult.data;
      if (csvData.length === 0) {
        throw new Error("No data found in CSV file");
      }

      setBulkUploadProgress(20);

      // Fetch required data
      const [borrowersRes, loanProductsRes, branchesRes] = await Promise.all([
        axios.get(
          `https://zedlenders.pickmesms.com/users?filter[role][name][_eq]=Borrower&filter[company]=${company.id}&fields=id,nrc,first_name,last_name&limit=-1`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `https://zedlenders.pickmesms.com/items/loan_products?filter[company]=${company.id}&filter[is_active][_eq]=true&fields=*`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `https://zedlenders.pickmesms.com/items/branches?filter[company]=${company.id}&fields=*`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
      ]);

      const borrowers = borrowersRes.data.data;
      const loanProducts = loanProductsRes.data.data;
      const branches = branchesRes.data.data;

      setBulkUploadProgress(40);

      const successes = [];
      const errors = [];
      const total = csvData.length;

      // Process each loan
      for (let i = 0; i < csvData.length; i++) {
        const row = csvData[i];
        try {
          // Find borrower by NRC
          const borrower = borrowers.find((b) => b.nrc === row.borrower_nrc);
          if (!borrower) {
            errors.push({
              row: i + 2,
              data: row,
              error: `Borrower with NRC "${row.borrower_nrc}" not found`,
            });
            continue;
          }

          // Find loan product by name
          const loanProduct = loanProducts.find(
            (p) => p.loan_name.toLowerCase() === row.loan_product_name.toLowerCase()
          );
          if (!loanProduct) {
            errors.push({
              row: i + 2,
              data: row,
              error: `Loan product "${row.loan_product_name}" not found`,
            });
            continue;
          }

          // Find branch by name (optional)
          let branchId = null;
          if (row.branch_name) {
            const branch = branches.find(
              (b) => b.branch_name.toLowerCase() === row.branch_name.toLowerCase()
            );
            if (branch) {
              branchId = branch.id;
            }
          }

          // Validate custom interest rate if provided
          let customRate = null;
          if (row.custom_interest_rate && row.custom_interest_rate.trim() !== "") {
            customRate = parseFloat(row.custom_interest_rate);

            // Validate rate is within allowed range if product allows custom rates
            if (loanProduct.allow_custom_interest_rate) {
              if (customRate < loanProduct.interest_rate_min || customRate > loanProduct.interest_rate_max) {
                errors.push({
                  row: i + 2,
                  data: row,
                  error: `Custom interest rate ${customRate}% is outside allowed range [${loanProduct.interest_rate_min}%-${loanProduct.interest_rate_max}%]`,
                });
                continue;
              }
            } else {
              errors.push({
                row: i + 2,
                data: row,
                error: `Loan product "${row.loan_product_name}" does not allow custom interest rates`,
              });
              continue;
            }
          }

          const amountIsMonthly = isMonthlyAmountRow(row);
          const rawAmount = parseFloat(row.amount);
          const monthlyAmount = parseFloat(
            row.monthly_amount || row.installment_amount || row.amount
          );

          if (!Number.isFinite(rawAmount) && !Number.isFinite(monthlyAmount)) {
            errors.push({
              row: i + 2,
              data: row,
              error: "Missing or invalid amount",
            });
            continue;
          }

          let loanAmountValue = rawAmount;
          if (amountIsMonthly) {
            if (!Number.isFinite(monthlyAmount)) {
              errors.push({
                row: i + 2,
                data: row,
                error: "Missing or invalid monthly_amount",
              });
              continue;
            }

            try {
              loanAmountValue = calculatePrincipalFromPayment(
                loanProduct,
                monthlyAmount,
                customRate ?? loanProduct.interest_rate
              );
            } catch (calcError) {
              errors.push({
                row: i + 2,
                data: row,
                error: `Failed to derive loan amount from monthly payment: ${calcError.message}`,
              });
              continue;
            }
          }

          // Calculate loan payments
          const paymentDetails = calculateLoanPayments(
            loanProduct,
            loanAmountValue
          );

          if (amountIsMonthly) {
            paymentDetails.next_payment_amount = Number(monthlyAmount.toFixed(2));
          }

          // Calculate expected return amount using FV formula
          const effectiveInterestRate = customRate
            ? customRate / 100
            : loanProduct.interest_rate / 100;

          const loanAmount = Number(loanAmountValue);
          const expectedReturnAmount = loanAmount * (1 + effectiveInterestRate);
          const profitAmount = expectedReturnAmount - loanAmount;

          // Parse loan_status from CSV (default to 1 - Pending Approval if not provided)
          const loanStatus = row.loan_status ? parseInt(row.loan_status) : 1;

          // Create loan payload
          const loanPayload = {
            borrower: borrower.id,
            loan_product: loanProduct.id,
            amount: loanAmount,
            custom_interest_rate: customRate, // Store the custom rate if provided
            interest_applied: customRate ?? loanProduct.interest_rate, // Store the actual rate used
            application_date: row.application_date,
            loan_purpose: row.loan_purpose,
            branch: branchId,
            company: company.id,
            loan_status: loanStatus,
            status: "published",
            next_payment_date: paymentDetails.next_payment_date,
            next_payment_amount: paymentDetails.next_payment_amount,
            total_amount_due: paymentDetails.total_amount_due,
            interest_amount: paymentDetails.interest_amount,
            expected_return_amount: expectedReturnAmount,
            profit_amount: profitAmount,
            ...(row.approval_date && { approval_date: row.approval_date }),
            ...(loanProduct.auto_split_payments &&
              paymentDetails.payment_schedule && {
                payment_schedule: paymentDetails.payment_schedule,
              }),
          };

          // Create the loan
          await axios.post(
            "https://zedlenders.pickmesms.com/items/loans",
            loanPayload,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          successes.push({
            row: i + 2,
            borrower: `${borrower.first_name} ${borrower.last_name}`,
            amount: row.amount,
          });
        } catch (err) {
          errors.push({
            row: i + 2,
            data: row,
            error: err.response?.data?.errors?.[0]?.message || err.message,
          });
        }

        // Update progress
        const progress = 40 + ((i + 1) / total) * 50;
        setBulkUploadProgress(Math.round(progress));
      }

      setBulkUploadProgress(100);

      // Show results
      if (successes.length > 0) {
        message.success(
          `Successfully created ${successes.length} loan(s)!`,
          5
        );
      }

      if (errors.length > 0) {
        console.error("Bulk upload errors:", errors);
        message.warning(
          `${errors.length} loan(s) failed to upload. Check console for details.`,
          5
        );
      }

      // Close modal and refresh page
      setTimeout(() => {
        setShowBulkUploadModal(false);
        setBulkUploadFile(null);
        setBulkUploadProgress(0);
        setBulkUploadLoading(false);
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error("Bulk upload error:", error);
      message.error(
        `Failed to process bulk upload: ${error.message}`,
        5
      );
      setBulkUploadLoading(false);
      setBulkUploadProgress(0);
    }
  }
};

export default LoansPage;
